import { Reducer } from 'react';

import { ICartPrice } from '@model';

import {
  IProduct,
  IServices,
  IGiftActions,
  IGiftPackaging,
  IEcoPackaging,
  IInsurance,
  IDiscount,
  IBenefits,
  IFundRaising,
  IDiscoveryBox,
} from '../../../model';

interface IPriceReducerAction {
  pricesSummary: ICartPrice;
}

export interface ICartPageState extends IPriceReducerAction {
  benefits: IBenefits;
  products: IProduct[];
  discounts: IDiscount[];
  serviceItems: IServices;
  gifts: IGiftActions[];
  proceedCheckoutEnabled: boolean;
  copiedProductVoucherCode: string;
  loyaltyRewardRemoved: boolean;
}

export interface ICartPageUpdateCompleteStateReducerAction extends IPriceReducerAction {
  benefits: IBenefits;
  products: IProduct[];
  discounts: IDiscount[];
  serviceItems: IServices;
  gifts: IGiftActions[];
}

export interface IUpdateProductsInCartReducerAction extends IPriceReducerAction {
  benefits: IBenefits;
  products: IProduct[];
  discounts: IDiscount[];
  gifts: IGiftActions[];
  serviceItems: { giftPackagings: IGiftPackaging[]; ecoPackaging: IEcoPackaging | null };
}

export interface IUpdateNonStandardProductsInCartReducerAction extends IPriceReducerAction {
  benefits: IBenefits;
  products: IProduct[];
  discounts: IDiscount[];
  serviceItems: { giftPackagings: IGiftPackaging[] };
}

export interface IToggleGiftPackagingReducerAction extends IPriceReducerAction {
  giftPackagings: IGiftPackaging[];
}

export interface IToggleEcoPackagingReducerAction extends IPriceReducerAction {
  ecoPackaging: IEcoPackaging | null;
}

export interface IToggleInsuranceReducerAction extends IPriceReducerAction {
  insurance: IInsurance | null;
}

export interface IToggleFundRaisingReducerAction extends IPriceReducerAction {
  fundRaising: IFundRaising | null;
}

export interface IRemoveDiscoveryBoxReducerAction extends IPriceReducerAction {
  products: IProduct[];
  discoveryBox: IDiscoveryBox[];
}

export interface IUpdateGiftSelectionReducerAction {
  giftProductId: number;
  giftListId: number;
  giftItemId?: number;
}

export interface IUpdateVoucherReducerAction extends IPriceReducerAction {
  products: IProduct[];
  benefits: IBenefits;
}

export interface IUpdateBenefitsAction extends IPriceReducerAction {
  benefits: IBenefits;
  discounts: IDiscount[];
}

export interface ISetProductVoucherCodeReducerAction {
  productVoucherCode: string;
}

export interface ISetRemovedLoyaltyRewardReducerAction {
  loyaltyRewardRemoved: boolean;
}

export enum ECartActions {
  UPDATE_COMPLETE_STATE,
  UPDATE_PRODUCTS,
  UPDATE_NON_STANDARD_PRODUCTS,
  TOGGLE_GIFT_PACKAGING,
  TOGGLE_ECO_PACKAGING,
  TOGGLE_INSURANCE,
  UPDATE_GIFT_SELECTION,
  UPDATE_VOUCHER,
  TOGGLE_FUND_RAISING,
  REMOVE_DISCOVERY_BOX,
  UPDATE_BENEFITS,
  SET_COPIED_PRODUCT_VOUCHER_CODE,
  SET_REMOVED_LOYALTY_REWARD,
}

export type CartActions =
  | { actionType: ECartActions.UPDATE_COMPLETE_STATE; payload: ICartPageUpdateCompleteStateReducerAction }
  | { actionType: ECartActions.UPDATE_PRODUCTS; payload: IUpdateProductsInCartReducerAction }
  | { actionType: ECartActions.UPDATE_NON_STANDARD_PRODUCTS; payload: IUpdateNonStandardProductsInCartReducerAction }
  | { actionType: ECartActions.TOGGLE_GIFT_PACKAGING; payload: IToggleGiftPackagingReducerAction }
  | { actionType: ECartActions.TOGGLE_ECO_PACKAGING; payload: IToggleEcoPackagingReducerAction }
  | { actionType: ECartActions.TOGGLE_INSURANCE; payload: IToggleInsuranceReducerAction }
  | { actionType: ECartActions.UPDATE_GIFT_SELECTION; payload: IUpdateGiftSelectionReducerAction }
  | { actionType: ECartActions.UPDATE_VOUCHER; payload: IUpdateVoucherReducerAction }
  | { actionType: ECartActions.TOGGLE_FUND_RAISING; payload: IToggleFundRaisingReducerAction }
  | { actionType: ECartActions.REMOVE_DISCOVERY_BOX; payload: IRemoveDiscoveryBoxReducerAction }
  | { actionType: ECartActions.UPDATE_BENEFITS; payload: IUpdateBenefitsAction }
  | { actionType: ECartActions.SET_COPIED_PRODUCT_VOUCHER_CODE; payload: ISetProductVoucherCodeReducerAction }
  | { actionType: ECartActions.SET_REMOVED_LOYALTY_REWARD; payload: ISetRemovedLoyaltyRewardReducerAction };

export type CartPageReducer = Reducer<ICartPageState, CartActions>;
